import { FCC } from "@/types/common";
import { UseVisibleReturn } from "@/hooks/useVisible";
import { Stack, Typography, Modal, styled, Box } from "@mui/material";
import { background, primary } from "@/theme/colors";
import { Checkbox } from "@/components/elements/Checkbox";
import { IconBox } from "@/components/elements/IconBox";
import { IconCloseModal } from "@/assets/icons";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { InfiniteScroll } from "@/components/elements/InfiniteScroll";
import { useMemo, useState } from "react";
import { useInformationList } from "@/api/services/main/information";
import { NotificationListModal } from "@/features/information/components/NotificationListModal";

type TNotificationModalProps = {
  modal: UseVisibleReturn;
};

export const NotificationModal: FCC<TNotificationModalProps> = ({ modal }) => {
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const {
    dataFormatter: informationListData,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useInformationList({
    types: ["case_comment"],
  });

  const filteredData = useMemo(() => {
    if (!showUnreadOnly) return informationListData;

    return informationListData.filter(
      (item) => !item.informationReads || item.informationReads.length === 0
    );
  }, [showUnreadOnly, informationListData]);


  const onClose = () => {
    modal.close();
  };

  return (
    <Modal open={modal.visible} hideBackdrop={false} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-start", marginTop: "55px" }}>
      <ModalContainer>
        <StyledModalHead>
          <StyledContentHead>
            <Typography variant="head20Bold">通知</Typography>
            <Stack>
              <Checkbox
                label={
                  <Typography variant="sub16Semi">
                    未読のみ
                  </Typography>
                }
                checked={!!showUnreadOnly}
                labelColor="white"
                onChange={(e) => setShowUnreadOnly(e.target.checked)}
              />
            </Stack>
            <IconBox
              size={24}
              sx={{
                position: "absolute",
                top: "calc(50% - 10px)",
                right: "19px",
                color: "white",
                "&:hover": {
                  backgroundColor: "transparent",
                },
              }}
              onClick={onClose}
            >
              <IconCloseModal />
            </IconBox>
          </StyledContentHead>
        </StyledModalHead>
        {!informationListData || isLoading ? (
          <LoadingArea height={100} />
        ) : (
          <ScrollArea>
            <InfiniteScroll loadMore={fetchNextPage} hasMore={hasNextPage} useWindow={false}>
              <NotificationListModal data={filteredData} modal={modal} />
            </InfiniteScroll>
          </ScrollArea>
        )}
      </ModalContainer>
    </Modal>
  );
};

const ModalContainer = styled(Box)`
  max-width: 640px;
  width: 100%;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  height: 697px;
  outline: none;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  &:focus {
    outline: none;
  }
`;

const StyledModalHead = styled(Stack)`
  flex-shrink: 0;
  z-index: 100;
  width: 100%;
`;

const ScrollArea = styled(Stack)`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const StyledContentHead = styled(Stack)`
  position: relative;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 24px 59px 24px 19px;
  background-color: ${primary.main};
  color: white;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  .MuiCheckbox-root {
    margin-top: 4px;
    background-color: white !important;
  },
`;
